import React from 'react';
import {get as _get} from 'lodash';
import moment from 'moment/moment';

import {
    getTerritoriesWithTooltipForDeviceCategories,
    parseDeviceCategories,
    renderDeviceCategories,
    renderNumberOfCountries,
} from '@utils/countryHelpers';
import {rowContainsColumn} from '@modules/events/utils/eventSchedule';
import {renderBlackoutZonesTableRowCell} from '@utils/blackoutZones';
import LinkWithAuthorization from '@appComponents/LinkWithAuthorization';
import {convertToInt, formattedPriceOrEmptyValue} from '@utils/helpers';
import {EventContentVariantPopup} from '@utils/eventContentVariant/EventContentVariantPopup';
import {IconCheckmark, IconWithTooltip} from '@appComponents/IconCollection';
import {EMPTY_VALUE_DASH} from '@constants/variables';
import {getLink} from '@appComponents/Link';
import EventBookingCancellationIcon from '@modules/booking/views/BookingCancellationIcon';
import {refetchQueryByName} from '@utils/apollo';
import {renderCategoryLevel, renderInvoiceStatusColumn} from '@modules/client/utils/clientBookingTable';
import Authorization from '@appComponents/Authorization';
import {ToggleIsCancelledBooking} from '@graphql/booking/mutation';
import {
    authorizationObjectBooking,
    authorizationObjectClientPackage,
    authorizationObjectEvent,
} from '@modules/client/components/ClientBookings/Table/authorizationObjects';
import {renderDescription} from '@utils/tableHelpers';

export const ClientBookingAndOfferingRowRenderer = (column, row, {mappedIsoCodes, clientId, history, type}) => {
    const geoRestrictions = _get(row, `${type}_geo_restrictions`, []),
        deviceCategoriesData = parseDeviceCategories(mappedIsoCodes, geoRestrictions);

    const eventContentVariant = {
        id: row.event_content_variant_id,
        name: row.event_content_variant_name,
        short_name: row.event_content_variant_short_name,
    };

    switch (column) {
        case 'id':
        case `${type}_id`:
        case 'match_or_stage_id':
        case 'event_content_id':
        case 'distribution_id':
        case 'content_tier_name':
        case 'product_short_name':
        case `${type}_type_name`:
        case 'distribution_type_name':
        case `${type}_creation_datetime`:
            return row[column];
        case 'event_id':
            return row.event_id;
        case 'start_datetime':
            return (
                <div>
                    <div>
                        {rowContainsColumn(column, row)
                            ? moment(row.start_datetime, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD HH:mm')
                            : ''
                        }
                    </div>
                    <div>
                        {rowContainsColumn('end_datetime', row)
                            ? moment(row.end_datetime, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD HH:mm')
                            : ''
                        }
                    </div>
                </div>
            );
        case 'content_category_level_1_name':
            return renderCategoryLevel(row, 1);
        case 'content_category_level_2_name':
            return renderCategoryLevel(row, 2);
        case 'content_category_level_3_name':
            return renderCategoryLevel(row, 3);
        case 'geo_restrictions_device':
            return renderDeviceCategories(deviceCategoriesData);
        case 'geo_restrictions_country':
            return getTerritoriesWithTooltipForDeviceCategories(deviceCategoriesData, mappedIsoCodes);
        case 'geo_restrictions_country_count':
            return renderNumberOfCountries(deviceCategoriesData);
        case 'blackoutZones':
            return renderBlackoutZonesTableRowCell(deviceCategoriesData, false);
        case 'competitors':
            return (
                <div>
                    <LinkWithAuthorization
                        authorization={authorizationObjectEvent}
                        link={{
                            name: 'events.configuration.index',
                            params: {id: convertToInt(row.event_id)},
                        }}
                    >
                        {row.is_stage_event ? row.stage_name_with_tooltip : null}
                        {!row.is_stage_event ? row.competitors : null}
                    </LinkWithAuthorization>

                    {rowContainsColumn(['event_description', 'event_external_description'], row) && (
                        <div>
                            {renderDescription(row.event_description, row.event_color, row.event_external_description)}
                        </div>
                    )}
                </div>
            );
        case 'event_content_type_name':
            return (
                <>
                    {row.event_content_type_name} {row.event_content_name ? ' - ' + row.event_content_name : ''}
                    <EventContentVariantPopup eventContentVariant={eventContentVariant}/>
                </>
            );
        case 'client_package_name':
            return (
                <LinkWithAuthorization
                    authorization={authorizationObjectClientPackage}
                    link={{
                        name: 'clients.packages.edit',
                        params: {
                            id: clientId,
                            packagesId: convertToInt(row.client_package_id),
                        },
                    }}
                >
                    {row.client_package_name}
                </LinkWithAuthorization>
            );
        case 'is_hq':
            return row.is_hq ? <IconCheckmark/> : null;
        case 'is_ll':
            return row.is_ll ? <IconCheckmark/> : null;
        case 'price':
            return formattedPriceOrEmptyValue(row.price, EMPTY_VALUE_DASH);
        case 'event_invoice_status_name':
            return renderInvoiceStatusColumn(row);
        case 'actions':
            return (
                <Authorization authorization={authorizationObjectBooking}>
                    <div className='actions-list-icon'>
                        <IconWithTooltip
                            iconProps={{
                                size: 'large',
                                className: '--withPointer',
                                corner: 'top right',
                            }}
                            name='edit'
                            onClick={() => {
                                history.push(getLink('clients.bookings.edit',
                                    {id: clientId, eventId: row.event_id, bookingId: row.booking_id}));
                            }}
                            tooltip={'General information'}
                            popupProps={{position: 'top right'}}
                        />
                        <IconWithTooltip
                            iconProps={{
                                size: 'large',
                                className: '--withPointer',
                                corner: 'top right',
                            }}
                            name='globe'
                            onClick={() => {
                                history.push(getLink('clients.bookings.editGeoRestrictions', {
                                    id: clientId, bookingId: row.booking_id,
                                }));
                            }}
                            tooltip={'Geo restrictions'}
                            popupProps={{position: 'top right'}}
                        />
                        <IconWithTooltip
                            iconProps={{
                                size: 'large',
                                className: '--withPointer',
                                corner: 'top right',
                            }}
                            name='shield'
                            onClick={() => {
                                history.push(getLink('clients.bookings.blackoutZones', {
                                    id: clientId, bookingId: row.booking_id,
                                }));
                            }}
                            tooltip={'Blackout Zones'}
                            popupProps={{position: 'top right'}}
                        />
                        <EventBookingCancellationIcon
                            bookingId={convertToInt(row.booking_id)}
                            isCancelled={row.is_cancelled}
                            entityName={'booking'}
                            messageBoxName='clientMessage'
                            successCallback={() => {
                                refetchQueryByName('clientBookingsForTable');
                            }}
                            mutationChangeCancellationStatus={ToggleIsCancelledBooking}
                        />
                    </div>
                </Authorization>
            );
        default:
            return null;
    }
};
